/**
 * FieldHelpers — piezas compartidas del asistente de residentes.
 *
 * Todo lo visual sale de wizard.module.css para que los cuatro pasos y los
 * dos modales se vean iguales: mismas etiquetas, mismos campos, mismos chips
 * y las mismas tarjetas de persona.
 */
import s from "./wizard.module.css";

// ── Sección con punto naranja, título, bajada y línea divisoria ─────────────
export function Section({ title, subtitle, action, children }) {
  return (
    <section className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <h4 className={s.sectionTitle}>
            <span className={s.sectionDot} />
            {title}
          </h4>
          {subtitle && <p className={s.sectionSub}>{subtitle}</p>}
        </div>
        {action && <div className={s.sectionAction}>{action}</div>}
      </div>
      {children && <div className={s.sectionBody}>{children}</div>}
    </section>
  );
}

// ── Error bajo un campo ─────────────────────────────────────────────────────
export function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div className={s.error}>
      <i className="fa-solid fa-circle-exclamation" />
      {msg}
    </div>
  );
}

// ── Envoltura etiqueta + control + ayuda + error ────────────────────────────
export function Field({
  label,
  required = false,
  optional = false,
  hint,
  help,
  error,
  children,
}) {
  return (
    // data-has-error lo usa el armazón para llevar la vista al primer problema.
    <div className={s.field} data-has-error={error ? "true" : undefined}>
      {(label || hint) && (
        <div className={s.labelRow}>
          <span className={s.label}>
            {label}
            {required && <span className={s.req}>*</span>}
            {optional && <span className={s.opt}>(opcional)</span>}
          </span>
          {hint}
        </div>
      )}
      {children}
      {help && <div className={s.help}>{help}</div>}
      <FieldError msg={error} />
    </div>
  );
}

// Indicadores que se dibujan dentro del campo (check verde, spinner, candado)
function FieldIcons({ ok, busy, locked }) {
  return (
    <>
      {locked && (
        <span className={`${s.fieldIcon} ${s.iconLock}`}>
          <i className="fa-solid fa-lock" />
        </span>
      )}
      {busy ? (
        <span className={`${s.fieldIcon} ${s.iconBusy}`}>
          <i className="fa-solid fa-circle-notch fa-spin" />
        </span>
      ) : ok ? (
        <span className={`${s.fieldIcon} ${s.iconOk}`}>
          <i className="fa-solid fa-circle-check" />
        </span>
      ) : null}
    </>
  );
}

function controlClass({ error, ok, busy, locked, extra = "" }) {
  return [
    s.input,
    error ? s.inputError : "",
    ok || busy ? s.hasIcon : "",
    locked ? s.hasIconDouble : "",
    locked ? s.inputLocked : "",
    extra,
  ]
    .filter(Boolean)
    .join(" ");
}

// ── Campo de texto ──────────────────────────────────────────────────────────
export function TextField({
  label,
  required,
  optional,
  hint,
  help,
  error,
  ok,
  busy,
  locked,
  ...rest
}) {
  return (
    <Field label={label} required={required} optional={optional} hint={hint} help={help} error={error}>
      <div className={s.inputWrap}>
        <input
          className={controlClass({ error, ok, busy, locked })}
          readOnly={locked}
          {...rest}
        />
        <FieldIcons ok={ok} busy={busy} locked={locked} />
      </div>
    </Field>
  );
}

// ── Campo de selección ──────────────────────────────────────────────────────
export function SelectField({ label, required, optional, hint, help, error, children, ...rest }) {
  return (
    <Field label={label} required={required} optional={optional} hint={hint} help={help} error={error}>
      <select className={controlClass({ error, extra: s.select })} {...rest}>
        {children}
      </select>
    </Field>
  );
}

// ── Campo de texto largo ────────────────────────────────────────────────────
export function TextAreaField({ label, required, optional, hint, help, error, rows = 3, ...rest }) {
  return (
    <Field label={label} required={required} optional={optional} hint={hint} help={help} error={error}>
      <textarea className={controlClass({ error, extra: s.textarea })} rows={rows} {...rest} />
    </Field>
  );
}

// ── Cédula con botón de búsqueda en el registro ─────────────────────────────
export function CedulaField({
  label = "Cédula de Identidad",
  required = true,
  placeholder = "Ej: 1-1234-5678",
  value,
  onChange,
  onBlur,
  onBuscar,
  estado,
  error,
  help,
  name = "cedula",
}) {
  return (
    <Field label={label} required={required} help={help} error={error}>
      <div className={s.inputGroup}>
        <input
          name={name}
          type="text"
          className={controlClass({ error })}
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
        />
        <button
          type="button"
          className={s.groupBtn}
          onClick={onBuscar}
          disabled={estado === "buscando"}
          title="Buscar los datos en el Registro Nacional"
        >
          {estado === "buscando" ? (
            <i className="fa-solid fa-circle-notch fa-spin" />
          ) : (
            <>
              <i className="fa-solid fa-magnifying-glass" />
              Buscar
            </>
          )}
        </button>
      </div>
      <CedulaStatus estado={estado} />
    </Field>
  );
}

// ── Resultado de la búsqueda de cédula ──────────────────────────────────────
export function CedulaStatus({ estado }) {
  if (!estado || estado === "idle") return null;

  const cfg = {
    buscando: { color: "var(--text-secondary)", icon: "fa-circle-notch fa-spin", text: "Buscando…" },
    local: { color: "#10b981", icon: "fa-circle-check", text: "Datos cargados desde el registro local" },
    tse: { color: "#10b981", icon: "fa-circle-check", text: "Datos cargados desde el Registro Nacional" },
    no_encontrado: {
      color: "#fbbf24",
      icon: "fa-triangle-exclamation",
      text: "Cédula no encontrada. Escribí el nombre a mano.",
    },
    error: {
      color: "#f87171",
      icon: "fa-circle-exclamation",
      text: "No se pudo consultar el registro. Escribí el nombre a mano.",
    },
  }[estado];

  if (!cfg) return null;

  return (
    <div className={s.statusLine} style={{ color: cfg.color }}>
      <i className={`fa-solid ${cfg.icon}`} />
      {cfg.text}
    </div>
  );
}

// ── Chip de alternancia suelto (No tiene correo) ────────────────────────────
export function ChipToggle({ checked, onChange, label, icon }) {
  return (
    <label className={checked ? `${s.trayChip} ${s.trayChipOn}` : s.trayChip}>
      <input type="checkbox" className={s.chipInput} checked={!!checked} onChange={onChange} />
      {icon ? <i className={`fa-solid ${icon}`} /> : <span className={s.groupChipDot} />}
      {label}
    </label>
  );
}

// ── Persona física / sociedad ───────────────────────────────────────────────
export function TipoPersonaSwitch({ value, onChange, labels, ancho = false }) {
  const [fisica, sociedad] = labels || ["Persona física", "Sociedad"];
  return (
    <div className={ancho ? `${s.switch} ${s.switchWide}` : s.switch} role="group">
      <button
        type="button"
        className={!value ? `${s.switchBtn} ${s.switchOn}` : s.switchBtn}
        aria-pressed={!value}
        onClick={() => onChange(false)}
      >
        {fisica}
      </button>
      <button
        type="button"
        className={value ? `${s.switchBtn} ${s.switchOn}` : s.switchBtn}
        aria-pressed={!!value}
        onClick={() => onChange(true)}
      >
        {sociedad}
      </button>
    </div>
  );
}

// ── Placas (una persona puede tener varias) ─────────────────────────────────
// variant "grupo": campo, chip y basurero pegados, como en los pasos.
// variant "bandeja": cada placa en su propia bandeja, como en el modal.
export function VehiculosField({
  vehiculos = [],
  onChange,
  errors,
  textoAgregar,
  variant = "grupo",
}) {
  function cambiar(idx, campo, valor) {
    onChange(vehiculos.map((v, i) => (i === idx ? { ...v, [campo]: valor } : v)));
  }

  const agregar = () => onChange([...vehiculos, { placa: "", extranjera: false }]);
  const quitar = (idx) => onChange(vehiculos.filter((_, i) => i !== idx));

  const bandeja = variant === "bandeja";

  return (
    <div className={s.plateList}>
      {vehiculos.map((v, idx) => {
        const campo = (
          <input
            type="text"
            className={controlClass({ error: errors?.[idx] })}
            placeholder={v.extranjera ? "Placa extranjera" : "Ej: ABC-123"}
            value={v.placa}
            onChange={(e) => cambiar(idx, "placa", e.target.value.toUpperCase())}
          />
        );

        const chip = (
          <label
            className={
              bandeja
                ? v.extranjera
                  ? `${s.trayChip} ${s.trayChipOn}`
                  : s.trayChip
                : v.extranjera
                  ? `${s.groupChip} ${s.groupChipOn}`
                  : s.groupChip
            }
            title="Marcar como placa extranjera"
          >
            <input
              type="checkbox"
              className={s.chipInput}
              checked={!!v.extranjera}
              onChange={() => cambiar(idx, "extranjera", !v.extranjera)}
            />
            <span className={s.groupChipDot} />
            Extranjera
          </label>
        );

        return (
          <div key={idx}>
            {bandeja ? (
              <div className={s.plateTray}>
                {campo}
                {chip}
                <button
                  type="button"
                  className={s.trayBtn}
                  onClick={() => quitar(idx)}
                  title="Quitar placa"
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            ) : (
              <div className={`${s.inputGroup} ${s.plateRow}`}>
                {campo}
                {chip}
                <button
                  type="button"
                  className={`${s.groupBtn} ${s.groupBtnDanger}`}
                  onClick={() => quitar(idx)}
                  title="Quitar placa"
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            )}
            <FieldError msg={errors?.[idx]} />
          </div>
        );
      })}

      <button type="button" className={s.btnDashed} onClick={agregar}>
        <i className={`fa-solid fa-plus ${s.btnDashedIcon}`} />
        {vehiculos.length ? textoAgregar?.[1] || "Agregar otra placa" : textoAgregar?.[0] || "Agregar placa"}
      </button>
    </div>
  );
}

// ── Tarjeta de persona (copropietarios y autorizados) ───────────────────────
export function iniciales(nombre) {
  const partes = String(nombre || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!partes.length) return "--";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}

export function PersonaCard({ nombre, avatar, pill, meta, chips, onEdit, onDelete, anidada = false }) {
  return (
    <div className={anidada ? `${s.personCard} ${s.personNested}` : s.personCard}>
      <span className={s.avatar}>{avatar || iniciales(nombre)}</span>
      <div className={s.personInfo}>
        <div className={s.personName}>
          <span>{nombre || "Sin nombre"}</span>
          {pill}
        </div>
        {meta && <div className={s.personMeta}>{meta}</div>}
      </div>
      <div className={s.personActions}>
        {chips}
        {onEdit && (
          <button type="button" className={s.iconBtn} onClick={onEdit} title="Editar">
            <i className="fa-solid fa-pen" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className={`${s.iconBtn} ${s.iconBtnDanger}`}
            onClick={onDelete}
            title="Quitar"
          >
            <i className="fa-solid fa-trash" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Píldoras y chips de datos ───────────────────────────────────────────────
export function Pill({ tono = "brand", redonda = false, children }) {
  const clase =
    { brand: s.pillBrand, ok: s.pillOk, neutral: s.pillNeutral, danger: s.pillDanger }[tono] ||
    s.pillBrand;
  return <span className={`${s.pill} ${clase} ${redonda ? s.pillRound : ""}`}>{children}</span>;
}

export function ChipDato({ icon, children }) {
  return (
    <span className={s.chipData}>
      {icon && <i className={`fa-solid ${icon}`} />}
      {children}
    </span>
  );
}

export function EmptyState({ children }) {
  return <div className={s.empty}>{children}</div>;
}
